import axios from 'axios';
import { INSTANCE_CONFIG, ERROR_CODE_MESSAGE } from '@/utils/common';
import {
  doLogout,
  getAuthorization,
  getBasePath,
  setLogin,
  storage,
} from '@/utils/utils';
import type { ResultType, CommonErrorType } from '@/types/axios';
import log from '@/log/aegis';
import { showErrorMessagetip } from '@/pages/home/components/ErrorMessageTip/ErrorMessageTip';
import { LOGIN_14_DAYS_KEY } from '@/pages/home/_config/config'; 

const requestIdInfo = {} as any;
const instance = axios.create(INSTANCE_CONFIG);

instance.interceptors.request.use(
  (config) => {
    const { headers = {}, url = '', ignoreRequestId = false } = config;
    if (checkFresh()) {
      const freshKey = 'cacheRefresh';
      config.url = `${url}${url.indexOf('?') > -1 ? '&' : '?'}${freshKey}=true`;
    }
    if (!ignoreRequestId) {
      const { url = '' } = config;
      if (!requestIdInfo[url]) {
        requestIdInfo[url] = 1;
      } else {
        requestIdInfo[url] += 1;
      }
      config.requestId = requestIdInfo[url];
    }
 

    // 设置用户信息请求头
    const Authorization = getAuthorization();
    config.headers = Authorization ? { Authorization, ...headers } : headers;

    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  (response) => {
    const { headers, data, status, config } = response;
    const { code, msg, data: realData } = (data || {}) as ResultType;
    const {
      noAuth,
      errorHandler,
      noReport = true,
      original,
      rawData,
      rejectRawData,
      formatData,
      url,
      method,
      params,
      data: requestParams,
      ignoreRequestId = true,
      requestId,
      responseType,
      setAuth = false,
      noRefresh = false,
      ignoreCode = false,
      // @ts-ignore
      download,
    } = config || {};

    // 这里是处理文件流下载，抛出错误
    const { 'content-type': responseContentType } = headers || {};
    // 文件流下载
    if (download && responseType === 'blob') {
      if (responseContentType === 'application/octet-stream') {
        // 接口返回 content-type 为 application/octet-stream 代表下载成功
        return response;
      } 
      // 否则 代表下载失败
      return Promise.reject(response);
    }

    // console.log('request response:', rawData, config)

    if (url && !ignoreRequestId && requestIdInfo[url] !== requestId) {
      return Promise.reject({ code: -998, msg: 'request id不符，数据失效' });
    }

    switch (true) {
      // 204状态码直接返回空数据
      case status === 204: {
        return null;
      }
      // 正常数据
      case status === 200 &&
        (code === 200 ||
          code === 0 ||
          responseType === 'arraybuffer' ||
          ignoreCode): {
        if (setAuth) {
          const { authorization } = headers;
          if (authorization) {
            setLogin(authorization);
          }
        }
        // getUid(headers);
        if (original) return response;
        if (rawData) {
          return data;
        }
        if (formatData) {
          return formatData(realData);
        }
        return realData;
      }
      // 登录态失效
      case status === 401 && !noAuth: {
        const pathname = window.location.pathname.toLowerCase();
        // 登录和退订页面特殊处理
        if (
          !pathname.startsWith(`${getBasePath()}/login`) &&
          pathname.indexOf('unsubscribe') < 0
        ) {
          // 14天免登陆逻辑
          const login14Days = storage.getItem(LOGIN_14_DAYS_KEY);
          if (login14Days && !noRefresh) {
            return refreshToken().then(
              () => {
                const { authorization, Authorization, ...restHeaders} = config?.headers || {};
                return instance({ ...config, headers: restHeaders, noRefresh: true });
              },
              () => {
                doLogout();
                return Promise.reject(data);
              },
            );
          } else {
            doLogout();
          }
        }
        return Promise.reject(data);
      }
      default: { 
        if (!errorHandler) {
          showCommonError({
            status,
            code,
            msg: ERROR_CODE_MESSAGE[code] || msg || ERROR_CODE_MESSAGE[999],
          });
        }
        if (!noReport) {
          log.error({
            url,
            method,
            params: requestParams || params,
            status,
            data,
          });
        }
        return Promise.reject(rejectRawData ? response : data);
      }
    }
  },
  (error) => {
    const { config, message, response } = error;
    const { status, data } = response ?? {}; // 当接口超时时，response会为undefined情况
    const errorMsg =
      ERROR_CODE_MESSAGE[data?.code] ||
      data?.msg ||
      message ||
      ERROR_CODE_MESSAGE[999];

    const {
      rejectRawData,
      errorHandler,
      noReport = true,
      url,
      method,
      data: requestParams,
      params,
    } = config || {};

    if (!errorHandler && !axios.isCancel(error)) {
      showCommonError({
        status,
        code: data?.code,
        msg: errorMsg,
      });
    }
    if (!noReport) {
      log.error({
        url,
        method,
        params: requestParams || params,
        status,
        data,
        message,
      });
    }
    return Promise.reject(
      rejectRawData ? error : { code: data?.code || 999, msg: errorMsg },
    );
  },
);

let sessionRefreshToken: Promise<any>;
let refreshTokenLock = false;
const refreshToken = () => {
  if (refreshTokenLock) {
    return sessionRefreshToken;
  }

  refreshTokenLock = true;
  sessionRefreshToken = instance({
    url: '/api/v1/auth/refreshToken',
    method: 'get',
    errorHandler: true,
    setAuth: true,
    noAuth: true,
  })
    .then(
      (data) => {
        return data;
      },
      (err) => {
        return Promise.reject(err);
      },
    )
    .finally(() => {
      setTimeout(() => {
        refreshTokenLock = false;
      }, 3000);
    });

  return sessionRefreshToken;
};

function checkFresh() {
  const str = '_refresh=1';
  if (window.location.search.indexOf(str) > -1) {
    return true;
  }
  if (window.location.hash.indexOf(str) > -1) {
    return true;
  }
  return false;
}

const showCommonError = (err: CommonErrorType) => {
  // 暂时
  return '';
  err.msg &&
    showErrorMessagetip({
      errorCode: err.code,
      content: err?.msg,
      duration: 5000,
      isClose: true,
      type: 'error',
    });
};

// export default instance;

// 封装requestId，如果request不符，设置Promise pending
export default (...values: any) =>
  new Promise((resolve, reject) => {
    // @ts-ignore
    instance(...values).then(
      (data) => {
        resolve(data);
      },
      (err) => {
        if (!checkRequestId(err)) {
          // todo 直接返回，没有resolve和reject,promise处于pending状态，不执行后续的then和catch
          return;
        }
        reject(err);
      },
    );
  });

const checkRequestId = (info: any) => {
  if (info?.code === -998) {
    return false;
  }
  return true;
};
